import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useBrand } from '../../branding/BrandProvider'
import { Search, X, Calendar, Users, ShoppingBag, MessageSquare, Clock, MapPin, Star, ArrowRight } from 'lucide-react'

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const brand = useBrand()

  const categories = [
    { id: 'all', name: 'All', icon: Search },
    { id: 'events', name: 'Events', icon: Calendar },
    { id: 'artists', name: 'Artists', icon: Users },
    { id: 'marketplace', name: 'Gear', icon: ShoppingBag },
    { id: 'forums', name: 'Forums', icon: MessageSquare },
  ]

  const searchResults = [
    {
      type: 'event',
      title: 'Jazz Night at The Wave',
      subtitle: 'Tonight • 8:00 PM • $15',
      icon: Calendar,
      rating: 4.9,
    },
    {
      type: 'artist',
      title: 'Sarah Mitchell',
      subtitle: 'Indie Folk • 1.2k followers',
      icon: Users,
      rating: 4.8,
    },
    {
      type: 'marketplace',
      title: 'Fender Stratocaster',
      subtitle: '$1,200 • Excellent condition',
      icon: ShoppingBag,
      rating: 4.9,
    },
  ]

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center pt-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-2xl mx-4"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="glass-card p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-display font-semibold text-white">
                  Search {brand.siteName}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 text-white/60 hover:text-white transition-colors duration-200"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Search Input */}
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search events, artists, gear, discussions..."
                  className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-4 py-4 text-white placeholder-white/40 focus:outline-none focus:border-le-accent transition-colors duration-200"
                  autoFocus
                />
              </div>

              {/* Categories */}
              <div className="flex space-x-2 mb-6 overflow-x-auto">
                {categories.map((category) => {
                  const Icon = category.icon
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all duration-200 ${
                        selectedCategory === category.id
                          ? 'bg-le-accent text-white'
                          : 'bg-white/10 text-white/70 hover:text-white hover:bg-white/20'
                      }`}
                    >
                      <Icon size={16} />
                      <span>{category.name}</span>
                    </button>
                  )
                })}
              </div>

              {/* Results */}
              {query && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {searchResults.map((result, index) => {
                    const Icon = result.icon
                    return (
                      <motion.div
                        key={index}
                        className="flex items-center space-x-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-200 cursor-pointer group"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <div className="w-10 h-10 bg-le-accent/20 rounded-lg flex items-center justify-center">
                          <Icon size={18} className="text-le-accent" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-white group-hover:text-le-accent transition-colors duration-200">
                            {result.title}
                          </h3>
                          <p className="text-sm text-white/60">{result.subtitle}</p>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-white/60">
                          <Star size={14} className="text-yellow-400" />
                          <span>{result.rating}</span>
                        </div>
                        <ArrowRight size={16} className="text-white/40 group-hover:text-le-accent transition-colors duration-200" />
                      </motion.div>
                    )
                  })}
                </div>
              )}

              {/* Quick Actions */}
              {!query && (
                <div className="space-y-4">
                  <h3 className="text-white font-medium">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button className="flex items-center space-x-3 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-200 text-left group">
                      <Calendar className="text-le-accent" size={20} />
                      <div>
                        <div className="font-medium text-white group-hover:text-le-accent transition-colors duration-200">Tonight's Events</div>
                        <div className="text-sm text-white/60">See what's happening</div>
                      </div>
                    </button>
                    <button className="flex items-center space-x-3 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-200 text-left group">
                      <ShoppingBag className="text-le-accent-2" size={20} />
                      <div>
                        <div className="font-medium text-white group-hover:text-le-accent-2 transition-colors duration-200">Latest Gear</div>
                        <div className="text-sm text-white/60">Browse marketplace</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SearchModal